import { IService, IShop, IOrderSearchResult } from '@app/common';
import { crudEndpointsFactory } from '../crud-endpoints.factory';
import { DateTime } from 'luxon';

function formatDateToDDMMYYYY(dateString: string | undefined | null): string {
  if (!dateString) return '';
  const date = DateTime.fromISO(dateString);
  if (!date.isValid) return '';
  return date.toFormat('dd/MM/yyyy');
}

// Déclenche le téléchargement d'un blob nommé, sans quitter la page — même geste que
// `suivreArchiveTelechargee`/`suivreExportTelecharge` (`fiscal.ts`) : un lien `<a>` éphémère,
// cliqué synchroniquement puis retiré.
function telechargerBlob(blob: Blob, nom: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  // L'ancre part tout de suite : le clic a déjà lancé le téléchargement. Seul
  // le relâchement de l'URL attend un tour de boucle (certains navigateurs le
  // veulent après le clic) — et il ne touche pas au document, qui peut avoir
  // disparu entre-temps (démontage, fin de test).
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// EXPORT COMPTABLE (E-07, chantier lot 6 tâches 12/13/14). Depuis la tâche 13, la réponse de
// `services/generate-csv-summary` porte `{ csv, startDate, endDate, controle, sceau }` :
// `controle` est le rapprochement au Grand Total signé (tâche 12, `AUCUN_REGISTRE` inclus —
// jamais un refus), `sceau` le hachage/signature du fichier RÉELLEMENT produit (tâche 13,
// `scelle: false` pour `AUCUN_REGISTRE`). Un refus 422 structuré (`PERIODE_NON_CLOTUREE`,
// `DIVERGENCE_GRAND_TOTAL`, `FENETRE_NON_ALIGNEE`) ne produit NI CSV NI sceau : son corps est
// rendu tel quel (voir `suivreExportTelecharge`, même raison) pour que `ServiceList.tsx`
// l'affiche en détail plutôt qu'une bannière générique.
//
// LES OCTETS DU CSV NE SONT JAMAIS MODIFIÉS (pas de BOM, pas d'ajout) : c'est exactement
// cette chaîne que l'engine a hachée (`sha256Hex(csv)`, tâche 13) — un BOM ajouté ici rendrait
// l'empreinte affichée invérifiable contre le fichier téléchargé. Le sceau ne va donc ni en
// trailer du CSV ni en fichier `.sig` : un second fichier compagnon (`…-scellement.json`)
// porte l'empreinte, la matière signée, la signature, le keyId (trousseau), la version du
// format de la trace (`formatVersion`, tâche 13), la période et le périmètre confronté.
// L'auteur du téléchargement (connu côté client, jamais attesté par le sceau) vit à part,
// sous `declarePar` : jamais un simple frère des champs cryptographiques.
export const suivreGenerationCsvSummary = (
  { shop, startDate, endDate, userEmail }: { shop: IShop; startDate: string; endDate: string; userEmail?: string },
) => async (response: Response) => {
  const data = await response.json();

  // Même garde que `suivreExportTelecharge` (`fiscal.ts`) : `fetchBaseQuery` appelle ce
  // `responseHandler` AVANT `validateStatus` — sans ce test, un refus 422 structuré (tâche 12)
  // se retrouvait « téléchargé » sous forme de CSV contenant un message d'erreur.
  if (!response.ok) return data;

  if (!data.csv) {
    throw new Error('Aucun CSV trouvé dans la réponse');
  }

  const formattedStartDate = formatDateToDDMMYYYY(startDate);
  const formattedEndDate = formatDateToDDMMYYYY(endDate);
  const baseName = `Résumé de service de ${shop.internalName} du ${formattedStartDate} au ${formattedEndDate}`;

  telechargerBlob(new Blob([data.csv], { type: 'text/csv;charset=utf-8;' }), `${baseName}.csv`);

  const sceau = data.sceau ?? {};
  const controle = data.controle ?? {};
  const scellement = {
    scelle: !!sceau.scelle,
    // Volet confronté au Grand Total signé : jamais une authenticité globale — la GT_JOUR ne
    // porte aucune ventilation par moyen de paiement (I5, tâche 12). `null` pour un fichier
    // non scellé (`AUCUN_REGISTRE`) : rien n'a été rapproché ni confronté.
    perimetre: sceau.scelle ? 'CREDIT_PAR_TAUX' : null,
    raison: sceau.raison ?? null,
    sha256: sceau.sha256 ?? null,
    matiere: sceau.signingInput ?? null,
    signature: sceau.signature ?? null,
    keyId: sceau.keyId ?? null,
    // Fix round 2 (revue) : version du format de la trace (T13) — absente pour un fichier
    // non scellé (`IFiscalAccountingSealNonScelle` ne la porte pas, `AUCUN_REGISTRE` ou
    // refus), présente sinon. Même champ que celui affiché sur la piste d'audit
    // (`FiscalAccountingExports.tsx`) : un auditeur qui compare les deux doit voir le
    // même schéma.
    formatVersion: sceau.formatVersion ?? null,
    gdh: sceau.gdh ?? null,
    periode: { from: startDate, to: endDate },
    registres: controle.registres ?? [],
    cloturesNF525: controle.cloturesNF525 ?? [],
    // Fix round 1 (revue) : `auteur` est connu côté CLIENT (utilisateur connecté), JAMAIS
    // attesté par le sceau lui-même — la signature ne porte que sur le CSV (`sha256`/
    // `matiere`/`signature`). Un simple champ frère de ces champs cryptographiques laissait
    // croire qu'il partageait leur garantie : imbriqué sous `declarePar`, avec sa propre
    // `source`, aucun lecteur ne peut confondre une déclaration du tableau de bord avec une
    // preuve signée.
    declarePar: { auteur: userEmail ?? null, source: 'tableau de bord (non signé)' },
  };

  telechargerBlob(
    new Blob([JSON.stringify(scellement, null, 2)], { type: 'application/json;charset=utf-8;' }),
    `${baseName}-scellement.json`,
  );

  return data;
};

export const serviceApi = crudEndpointsFactory<IService>()('services', 'Services').injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    generateSummary: builder.mutation<any, { startDate: string, endDate: string, withDetails: boolean, shop: IShop }>({
      query: (params) => ({
        url: 'services/generate-summary',
        method: 'GET',
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          withDetails: params.withDetails
        },
        responseHandler: async (response) => {
          const data = await response.json();
          if (data.pdf) {
            const base64 = data.pdf.split(',')[1];
            const binary = atob(base64);

            const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            // Use formatted dates in filename
            const formattedStartDate = formatDateToDDMMYYYY(params.startDate);
            const formattedEndDate = formatDateToDDMMYYYY(params.endDate);
            a.download = `Résumé de service de ${params.shop.internalName} du ${formattedStartDate} au ${formattedEndDate}.pdf`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }, 0);
          } else {
            throw new Error("Aucun PDF trouvé dans la réponse");
          }
        },
        cache: "no-cache",
      }),
    }),
    generateCsvSummary: builder.mutation<any, { startDate: string, endDate: string, shop: IShop, userEmail?: string }>({
      query: (params) => ({
        url: 'services/generate-csv-summary',
        method: 'GET',
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
        },
        responseHandler: suivreGenerationCsvSummary(params),
        cache: "no-cache",
      }),
      // Fix round 1 (revue, E-07) : un export ABOUTI écrit toujours une ligne d'audit
      // (tâche 13) — la page Conformité (`FiscalAccountingExports.tsx`) doit la voir sans
      // recharger la page. RTK Query n'invalide `invalidatesTags` que sur une mutation
      // `fulfilled` : un REFUS 422 (qui écrit AUSSI une ligne, best-effort, tâche 12) ne
      // passe jamais par ici — `ServiceList.tsx` refetch cette liste explicitement après
      // un refus structuré.
      invalidatesTags: [{ type: 'FiscalAccountingExports', id: 'LIST' }],
    }),
    searchOrders: builder.query<{ results: IOrderSearchResult[], hasMore: boolean }, { q: string, startDate: string, endDate: string }>({
      query: (params) => ({
        url: 'services/search-orders',
        method: 'GET',
        params,
      }),
    }),
  })
});

